"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useThreeScene = void 0;
var react_1 = require("react");
var realms_three_1 = __importDefault(require("realms-three"));
var useThreeScene = function (_a) {
    var scene = _a.scene, camera = _a.camera, canvasRef = _a.canvasRef, canvasWidth = _a.canvasWidth, canvasHeight = _a.canvasHeight, onSetupRenderer = _a.onSetupRenderer, onSetupScene = _a.onSetupScene, onTeardownScene = _a.onTeardownScene, beforeRender = _a.beforeRender, afterRender = _a.afterRender;
    var rendererRef = (0, react_1.useRef)(null);
    var effectComposerRef = (0, react_1.useRef)(null);
    var renderPassRef = (0, react_1.useRef)(null);
    var currentRenderIndexRef = (0, react_1.useRef)(0);
    // Set up renderer
    if (!rendererRef.current && canvasRef.current) {
        rendererRef.current = new realms_three_1.default.WebGLRenderer({
            canvas: canvasRef.current,
        });
        if (!effectComposerRef.current) {
            effectComposerRef.current = new realms_three_1.default.STDLIB.EffectComposer(rendererRef.current);
        }
        if (onSetupRenderer) {
            onSetupRenderer({
                renderer: rendererRef.current,
                composer: effectComposerRef.current,
                camera: camera,
            });
        }
    }
    // Update renderer and camera
    (0, react_1.useEffect)(function () {
        // Update camera
        var aspect = canvasWidth / canvasHeight;
        if (camera instanceof realms_three_1.default.PerspectiveCamera) {
            camera.fov = 45;
            camera.aspect = aspect;
            // We want to set this to a good value or
            // it will have jagged lines with effect composer.
            // See: https://discourse.threejs.org/t/using-effectcomposer-with-multiple-passes-causes-saw-tooth-near-geometry-intersections/19107/5
            camera.near = 5;
            camera.far = 20000;
        }
        if (camera instanceof realms_three_1.default.OrthographicCamera) {
            camera.left = canvasWidth / -50;
            camera.right = canvasWidth / 50;
            camera.top = canvasHeight / 50;
            camera.bottom = canvasHeight / -50;
            camera.near = -500;
            camera.far = 20000;
        }
        camera.updateProjectionMatrix();
        if (!rendererRef.current) {
            return;
        }
        rendererRef.current.setSize(canvasWidth, canvasHeight);
        rendererRef.current.setPixelRatio(window.devicePixelRatio);
        if (!effectComposerRef.current) {
            return;
        }
        effectComposerRef.current.setSize(canvasWidth, canvasHeight);
        effectComposerRef.current.setPixelRatio(rendererRef.current.getPixelRatio());
    }, [rendererRef, canvasWidth, canvasHeight, camera]);
    // Render the scene
    (0, react_1.useEffect)(function () {
        var currentIndex = currentRenderIndexRef.current;
        var render = function () {
            // New index so stop loop
            if (currentIndex !== currentRenderIndexRef.current) {
                return;
            }
            // Render
            if (rendererRef.current &&
                renderPassRef.current &&
                effectComposerRef.current &&
                scene) {
                // Before render hook
                if (beforeRender) {
                    beforeRender({
                        scene: scene,
                        camera: camera,
                        composer: effectComposerRef.current,
                        renderer: rendererRef.current,
                        canvasHeight: canvasHeight,
                        canvasWidth: canvasWidth,
                    });
                }
                // THREEJS Render
                renderPassRef.current.scene = scene;
                renderPassRef.current.camera = camera;
                effectComposerRef.current.render();
                if (afterRender) {
                    afterRender({
                        scene: scene,
                        camera: camera,
                        composer: effectComposerRef.current,
                        renderer: rendererRef.current,
                        canvasHeight: canvasHeight,
                        canvasWidth: canvasWidth,
                    });
                }
            }
            requestAnimationFrame(render);
        };
        render();
        return function () {
            currentRenderIndexRef.current += 1;
        };
    }, [scene, camera, canvasWidth, canvasHeight, beforeRender, afterRender]);
    (0, react_1.useEffect)(function () {
        if (rendererRef.current &&
            effectComposerRef.current &&
            scene &&
            onSetupScene) {
            var renderer_1 = rendererRef.current;
            var composer_1 = effectComposerRef.current;
            if (!renderPassRef.current) {
                renderPassRef.current = new realms_three_1.default.STDLIB.RenderPass(scene, camera);
                composer_1.addPass(renderPassRef.current);
            }
            onSetupScene({
                scene: scene,
                camera: camera,
                composer: composer_1,
                renderer: renderer_1,
                canvasWidth: canvasWidth,
                canvasHeight: canvasHeight,
            });
            return function () {
                if (onTeardownScene) {
                    onTeardownScene({
                        scene: scene,
                        camera: camera,
                        composer: composer_1,
                        renderer: renderer_1,
                        canvasHeight: canvasHeight,
                        canvasWidth: canvasWidth,
                    });
                }
            };
        }
        return;
    }, [scene, onSetupScene, onTeardownScene, canvasWidth, canvasHeight, camera]);
    return {
        renderer: rendererRef.current,
    };
};
exports.useThreeScene = useThreeScene;
